/** The four DOM chores this application does often enough to name. */

/**
 * Find an element that the page is required to contain.
 *
 * The type parameter appears only in the return type, which is exactly what
 * `no-unnecessary-type-parameters` objects to — but the alternative is every
 * one of the fifty call sites writing the same assertion, and an assertion at
 * the call site is no safer than one here.
 *
 * Throwing is right: a missing element means the HTML and the script have
 * drifted apart, and every later line would be working around a `null` that
 * should never occur. The message names the selector, which is the only thing
 * anybody needs in order to fix it.
 */
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-parameters -- see above
export function must<T extends Element>(selector: string, root: ParentNode = document): T {
  const found = root.querySelector<T>(selector);
  if (found === null) throw new Error(`The page is missing ${selector}.`);
  return found;
}

export function setText(element: Element, value: string): void {
  element.textContent = value;
}

/** Uses the `hidden` property rather than inline styles, so CSS stays in the stylesheet. */
export function show(element: HTMLElement, visible: boolean): void {
  element.hidden = !visible;
}

/**
 * Run `work` with `button` visibly busy, and hand back whatever it returns.
 *
 * The busy state is carried by `disabled` and a `data-busy` attribute that the
 * stylesheet renders — deliberately *not* by rewriting the caption. A caption
 * says what the button will do, and that is derived from server state on every
 * render; a helper that saved the caption and wrote it back afterwards would be
 * a second owner of the same string, and being the one that runs last it would
 * win. That is exactly how a stage toggle came to read "Open this stage" while
 * the stage it controls was already open, and then did the opposite of what it
 * said when pressed again.
 */
export async function whileBusy<T>(button: HTMLButtonElement, work: () => Promise<T>): Promise<T> {
  button.disabled = true;
  button.dataset['busy'] = 'true';
  try {
    return await work();
  } finally {
    button.disabled = false;
    delete button.dataset['busy'];
  }
}
